import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import "./SystemUsers.css";
import "../RolesSystem/RolesSystem.css";
import BtnDelete from "../../buttons/btnDelete.component/BtnDelete";
import EditIcon from "../../../assets/edit.svg";
import BtnAdd from "../../buttons/btnAdd.component/BtnAdd";
import { Context } from "../../../index";
import {
    IGetAllUsersWithSystemRolesChecked,
} from "../../../models/PersonModels/IGetAllUsersWithSystemRolesChecked";
import ArrowSub from "../../../assets/arrowDown.svg";
import ArrowRight from "../../../assets/ArrowRight.svg";
import ArrowUp from "../../../assets/arrow_up.svg";
import ArrowDown from "../../../assets/arrow_down.svg";
import Cross from "../../../assets/cancelGreey.svg";
import { InputText } from "primereact/inputtext";
import { IGetRole } from "../../../common/api_interfaces/role/IGetRole";
import { fullNameFormat } from "../../../helpers/Inicials";
import { AppContext } from "../../../App";
import Avatar, { AvatarSize } from "../../Shared/Avatar/Avatar";
import { dateToNiceString } from "../../../helpers/dateToNiceString";
import Loader from "../../Shared/Loader/Loader";
import { isAxiosError } from "axios";
import ConfirmationWindow from "../../Shared/ConfirmationWindow/ConfirmationWindow";
import { IUserStatus } from "../../../common/api_interfaces/user/IUserStatus";
import { IEditUserStatus } from "../../../common/api_interfaces/user/IEditUserStatus";
import { ISystemDeletePerson } from "../../../common/api_interfaces/user/ISystemDeletePerson";
import { ISystemInvitePerson } from "../../../common/api_interfaces/user/ISystemInvitePerson";
import { UserStatusCode } from "../../../common/enums/userStatus";

import { ReactComponent as DeletedStatus } from "../../../assets/images/addChekMark.svg";
import Button from "../../UiLib/Button/Button";

interface ISystemUsersSort {
    type?: SortType;
    asc: boolean;
    status?: IUserStatus;
}

enum SortType {
    name,
    role,
    dateEntry,
    dateUpdate,
    status,
}

const SystemUsers: FC = () => {
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);

    const [allUsers, setAllUsers] = useState<IGetAllUsersWithSystemRolesChecked[]>();
    const [allSaveUsers, setAllSaveUsers] = useState<IGetAllUsersWithSystemRolesChecked[]>();
    const [allRoles, setAllRoles] = useState<IGetRole[]>();
    const [currentEditUser, setCurrentEditUser] = useState<{
        id: number;
        systemRole: {
            id: number;
            name: string;
        };
        status: IUserStatus;
        personRolesId: number;
    }>();
    const [userStatuses, setUserStatuses] = useState<IUserStatus[]>([]);

    const [editWidgetShow, setEditWidgetShow] = useState<boolean>(false);
    const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false);
    const [showAddWidget, setShowAddWidget] = useState<boolean>(false);
    const [currentSort, setCurrentSort] = useState<ISystemUsersSort>({ type: undefined, asc: true });
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState<boolean>(false);

    const [email, setEmail] = useState<string>();
    const [firstName, setFirstName] = useState<string>();
    const [surname, setSurname] = useState<string>();
    const [middleName, setMiddleName] = useState<string>();
    const [roleNewUser, setRoleNewUser] = useState<number>(0);
    const [roleNewUserError, setRoleNewUserError] = useState<string>();
    const [emailError, setEmailError] = useState<string>();
    const [nameError, setNameError] = useState<string>();
    const [surnameError, setSurnameError] = useState<string>();

    const selectedUsersCount = useMemo(() => allUsers?.filter((u) => u.check).length ?? 0, [allUsers]);
    const showEditButton = useMemo(
        () =>
            allUsers &&
            allUsers?.filter(
                (u) =>
                    u.check &&
                    (u.user.status.code === UserStatusCode.Blocked ||
                        u.user.status.code === UserStatusCode.Active ||
                        u.user.status.code === UserStatusCode.PendingActivation)
            ).length === 1,
        [allUsers]
    );

    const showDeleteButton = useMemo(
        () =>
            allUsers &&
            allUsers?.filter(
                (u) =>
                    u.check &&
                    (u.user.status.code === UserStatusCode.PendingActivation ||
                        u.user.status.code === UserStatusCode.Invited)
            ).length > 0,
        [allUsers]
    );

    useEffect(() => {
        getUsers();
        getUserStatuses();
    }, []);

    useEffect(() => {
        if (!allUsers) return;
        const data: IGetAllUsersWithSystemRolesChecked[] = structuredClone(allUsers);
        if (currentSort.type === SortType.dateUpdate) {
            setAllUsers(
                data.sort((a, b) => {
                    const dateA = new Date(a.user.dateUpdate);
                    const dateB = new Date(b.user.dateUpdate);
                    if (dateA < dateB) return currentSort.asc ? -1 : 1;
                    else if (dateA > dateB) return currentSort.asc ? 1 : -1;
                    else return 0;
                })
            );
        } else if (currentSort.type === SortType.dateEntry) {
            setAllUsers(
                data.sort((a, b) => {
                    const dateA = a.user.dateLastIn !== "" ? new Date(a.user.dateLastIn) : null;
                    const dateB = b.user.dateLastIn !== "" ? new Date(b.user.dateLastIn) : null;

                    if (dateA === null && dateB === null) return 0;
                    if (dateA === null) return 1;
                    if (dateB === null) return -1;

                    if (dateA < dateB) return currentSort.asc ? -1 : 1;
                    else if (dateA > dateB) return currentSort.asc ? 1 : -1;
                    else return 0;
                })
            );
        } else if (currentSort.type === SortType.status) {
            const withStatus = data.filter((user) => user.user.status.code === currentSort.status?.code); //Фильтр по коду будет
            const withoutStatus = data.filter((user) => user.user.status.code !== currentSort.status?.code); //Фильтр по коду будет
            const sortedUsers = [...withStatus, ...withoutStatus];
            setAllUsers(sortedUsers);
        } else if (currentSort.type === SortType.role) {
            setAllUsers(
                data.sort((a, b) => {
                    if (a.user.systemRole.name < b.user.systemRole.name) return currentSort.asc ? -1 : 1;
                    else if (a.user.systemRole.name > b.user.systemRole.name) return currentSort.asc ? 1 : -1;
                    else return 0;
                })
            );
        } else if (currentSort.type === SortType.name) {
            setAllUsers(
                data.sort((a, b) => {
                    if (a.user.user.name < b.user.user.name) return currentSort.asc ? -1 : 1;
                    else if (a.user.user.name > b.user.user.name) return currentSort.asc ? 1 : -1;
                    else return 0;
                })
            );
        } else {
            setAllUsers(
                data.sort((a, b) => a.user.id - b.user.id)
            );
        }
    }, [currentSort]);

    const setSort = (newSort: SortType) => {
        if (currentSort.type === newSort && !currentSort.asc) setCurrentSort({ type: undefined, asc: true });
        else setCurrentSort({ type: newSort, asc: newSort === currentSort.type ? !currentSort.asc : true });
    };

    const setStatusSort = (statusCode: string | undefined) => {
        setCurrentSort({ type: statusCode !== undefined ? SortType.status : undefined, status: userStatuses.find((xx) => xx.code === statusCode), asc: true });
    };

    function getUserStatuses() {
        (async () => {
            try {
                //Ну если статусы не подгрузятся будет гг
                const statuses = await store.getUserAllStatuses();

                if (statuses?.length) {
                    setUserStatuses(statuses);
                }
            } catch (err) {
                console.log(err);
            }
        })();
    }

    function getUsers() {
        (async () => {
            try {
                const users = (await store.userService.getAllUsersWithSystemRoles()).data;
                setAllUsers(
                    users.map((xx) => {
                        return {
                            check: false,
                            user: xx,
                        };
                    })
                );
                const roles = await store.getNameAllRoles();
                setAllRoles(roles);
            } catch (err) {
                console.log(err);
            }
        })();
    }

    function viewBlock(idView: string, imageOne: string, imageTwo: string) {
        const element = document.getElementById(idView);
        const elementOne = document.getElementById(imageOne);
        const elementTwo = document.getElementById(imageTwo);

        if (element) {
            if (element.style.display === "none") {
                element.style.display = "block";
            } else {
                element.style.display = "none";
            }
        } else {
            console.error(`Элемент с ID ${idView} не найден.`);
        }

        if (elementOne) {
            if (elementOne.style.display === "none") {
                elementOne.style.display = "block";
            } else {
                elementOne.style.display = "none";
            }
        } else {
            console.error(`Элемент с ID ${elementOne} не найден.`);
        }

        if (elementTwo) {
            if (elementTwo.style.display === "none") {
                elementTwo.style.display = "block";
            } else {
                elementTwo.style.display = "none";
            }
        } else {
            console.error(`Элемент с ID ${elementTwo} не найден.`);
        }
    }

    function showDeleteFunc() {
        setShowDeleteMessage(true);
    }

    function deletePersonFunc() {
        let getPersonToBlock = allUsers?.filter((xx) => xx.check === true);
        if (getPersonToBlock) {
            (async () => {
                try {
                    const data: ISystemDeletePerson[] = getPersonToBlock.map((xx) => {
                        return {
                            userId: xx.user.id,
                        };
                    });
                    const delPers = await store.deleteSystemPerson(data);
                    getUsers();
                } catch (err) {
                    console.log(err);
                }
            })();
        }
        setShowDeleteMessage(false);
    }

    function closeAdd() {
        setFirstName("");
        setSurname("");
        setEmail("");
        setRoleNewUser(0);
        setNameError("");
        setSurnameError("");
        setMiddleName("");
        setEmailError("");
        setRoleNewUserError("");
        setShowAddWidget(false);
    }

    function addPersonFunc() {
        validateEmail();
        validateName();
        validateSurname();

        if (
            firstName?.length !== 0 &&
            surname?.length !== 0 &&
            email?.length !== 0 &&
            roleNewUser !== 0 &&
            (emailError?.length === 0 || emailError === undefined) &&
            (surnameError?.length === 0 || surnameError === undefined) &&
            (nameError?.length === 0 || nameError === undefined)
        ) {
            setShowLoader(true);
            setNameError("");
            setSurnameError("");
            setEmailError("");
            setRoleNewUserError("");

            let newUser: ISystemInvitePerson = {
                name: firstName!,
                surname: surname!,
                middleName: middleName!,
                email: email!,
                roleId: roleNewUser!,
            };

            (async () => {
                try {
                    await store.commonService.systemInviteUser(newUser);
                    getUsers();
                } catch (err) {
                    if (isAxiosError(err)) showToast(err.response?.data.message);
                } finally {
                    setShowLoader(false);
                }
            })();

            setFirstName("");
            setSurname("");
            setMiddleName("");
            setEmail("");
            setRoleNewUser(0);
            setShowAddWidget(false);
        } else {
            nameError?.length === 0 ? setNameError("Заполните имя") : setNameError("");
            surnameError?.length === 0 ? setSurnameError("Заполните фамилию") : setSurnameError("");
            emailError?.length === 0 ? setEmailError("Введите email") : setEmailError("");
            roleNewUser === 0 ? setRoleNewUserError("Выберите роль") : setRoleNewUserError("");
        }
    }

    function saveUser() {
        if (currentEditUser) {
            const editUser: IEditUserStatus = {
                id: currentEditUser.id,
                statusCode: currentEditUser.status.code,
                roleId: currentEditUser.systemRole.id,
                personRolesId: currentEditUser.personRolesId,
            };

            (async () => {
                try {
                    await store.commonService.editUser(editUser);
                    getUsers();
                } catch (err) {
                    if (isAxiosError(err)) showToast(err.response?.data.message);
                }
            })();
        }

        setCurrentEditUser(undefined);
        setAllSaveUsers(undefined);
        setEditWidgetShow(false);
    }

    function editPerson() {
        let currentUser = allUsers?.find((xx) => xx.check === true);
        if (currentUser) {
            const allSaveUsersCopy = JSON.parse(JSON.stringify(allUsers));
            setAllSaveUsers(allSaveUsersCopy);
            setCurrentEditUser({
                id: currentUser!.user.id,
                systemRole: {
                    id: currentUser!.user.systemRole.id,
                    name: currentUser!.user.systemRole.name,
                },
                status: currentUser!.user.status,
                personRolesId: currentUser!.user.personRolesId,
            });
            setEditWidgetShow(true);
        }
    }

    const validateName = (): boolean => {
        let nameErr = undefined;

        if (!firstName || firstName.length === 0) {
            nameErr = "Заполните имя";
        }

        setNameError(nameErr);
        return nameErr !== undefined;
    };

    const validateSurname = (): boolean => {
        let surnameErr = undefined;

        if (!surname || surname.length === 0) {
            surnameErr = "Заполните фамилию";
        }

        setSurnameError(surnameErr);
        return surnameErr !== undefined;
    };

    const validateEmail = (): boolean => {
        let emailErr = undefined;

        if (!email) {
            emailErr = "Введите email";
        } else if (!isValidEmail(email)) {
            emailErr = "Некорректный email";
        }

        setEmailError(emailErr);
        return emailErr !== undefined;
    };

    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };

    function closeFunc() {
        setCurrentEditUser(undefined);
        setAllUsers(allSaveUsers);
        setAllSaveUsers(undefined);
        setEditWidgetShow(false);
    }

    function changeRole(idRole: number, idPerson: number) {
        if (allUsers && allRoles) {
            const userToUpdate = allUsers.find((user) => user.user.id === idPerson);
            if (userToUpdate) {
                const newRole = allRoles.find((role) => role.id === idRole);

                if (newRole) {
                    userToUpdate.user.systemRole = { id: idRole, name: newRole.roleName };

                    if (currentEditUser) {
                        let data = currentEditUser;
                        data.systemRole.name = newRole.roleName;
                        data.systemRole.id = newRole.id;

                        setCurrentEditUser(data);
                    }

                    const editUser: IEditUserStatus = {
                        id: userToUpdate.user.id,
                        statusCode: userToUpdate.user.status.code,
                        roleId: userToUpdate.user.systemRole.id,
                        personRolesId: userToUpdate.user.personRolesId,
                    };

                    (async () => {
                        try {
                            await store.commonService.editUser(editUser);
                            getUsers();
                        } catch (err) {
                            if (isAxiosError(err)) showToast(err.response?.data.message);
                        }
                    })();
                } else {
                    console.error(`Role with ID ${idRole} not found.`);
                }
            } else {
                console.error(`User with ID ${idPerson} not found.`);
            }
        } else {
            console.error("Either allUsers or allRoles is undefined.");
        }
    }

    function dateToString(dateISO: string) {
        return dateISO ? dateToNiceString(new Date(dateISO), "dd.MM.yyyy в hh:mm") : "";
    }

    function changeStatus(check: boolean) {
        let blocked = userStatuses.find((xx) => xx.code === "Blocked");
        let active = userStatuses.find((xx) => xx.code === "Active");
        let pending = userStatuses.find((xx) => xx.code === "PendingActivation");
        let invited = userStatuses.find((xx) => xx.code === "Invited");

        if (!blocked || !active || !pending || !invited) {
            return;
        }
        let newStatus: IUserStatus | undefined = undefined;
        if (currentEditUser?.status.code === "Blocked") {
            newStatus = active;
        }
        if (currentEditUser?.status.code === "Active") {
            newStatus = blocked;
        }
        if (currentEditUser?.status.code === "Invited") {
            newStatus = blocked;
        }
        if (currentEditUser?.status.code === "PendingActivation") {
            newStatus = active;
        }

        let userNewState = JSON.parse(JSON.stringify(currentEditUser));
        userNewState.status = newStatus;

        setCurrentEditUser(userNewState);
    }

    function changeCheck(idUser: number, check: boolean) {
        const newData = allUsers?.map((xx) => {
            if (xx.user.id === idUser) {
                return {
                    ...xx,
                    check: check,
                };
            }
            return xx;
        });
        setAllUsers(newData);
    }

    function resetChecks() {
        const newData = allUsers?.map((user) => {
            user.check = false;
            return user;
        });
        setAllUsers(newData);
    }

    const handleRoleChange = (block: string, idRole: number, idUser: number) => {
        changeRole(idRole, idUser);
        viewBlock(block, "SystemGroupArrowSub" + String(idUser), "SystemGroupArrowRight" + String(idUser));
    };

    const handleRoleChangeWidget = (
        block: string,
        arrowSub: string,
        arrowRight: string,
        idRole: number,
        idUser: number
    ) => {
        changeRole(idRole, idUser);
        viewBlock(block, arrowSub, arrowRight);
    };

    const handleRoleChangeRoleNewPerson = (block: string, arrowSub: string, arrowRight: string, idRole: number) => {
        let findRole = allRoles?.find((xx) => xx.id === idRole);
        setRoleNewUser(findRole ? findRole.id : 0);
        viewBlock(block, arrowSub, arrowRight);
    };

    return (
        <div className="system--users__component">
            {showLoader ? <Loader /> : null}
            {showAddWidget ? (
                <div className="system--users__component--widget__Edit">
                    <div className="system--users__component--widget__add">
                        <div className="system--users__component--widget__add--header">
                            <div>Новый пользователь</div>
                            <div className="add__edit--role__component--header__img">
                                <img src={Cross} onClick={() => closeAdd()} />
                            </div>
                        </div>
                        <div className="system--users__component--widget__add--header__block">
                            <div className="system--users__component--widget__add--header__text">Имя</div>
                            <div className="system--users__component--widget__add--header__input">
                                <InputText
                                    id="login_first_name"
                                    value={firstName}
                                    placeholder="Имя"
                                    onBlur={validateName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                                />
                                {nameError ? (
                                    <p className="system--users__component--widget__add--header__input--error_message">
                                        {nameError}
                                    </p>
                                ) : null}
                            </div>
                            <div className="system--users__component--widget__add--header__text">Фамилия</div>
                            <div className="system--users__component--widget__add--header__input">
                                <InputText
                                    id="login_surname"
                                    value={surname}
                                    placeholder="Фамилия"
                                    onBlur={validateSurname}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSurname(e.target.value)}
                                />
                                {surnameError ? (
                                    <p className="system--users__component--widget__add--header__input--error_message">
                                        {surnameError}
                                    </p>
                                ) : null}
                            </div>
                            <div className="system--users__component--widget__add--header__text">Отчество</div>
                            <div className="system--users__component--widget__add--header__input">
                                <InputText
                                    id="middle_name"
                                    value={middleName}
                                    placeholder="Отчество"
                                    autoComplete="off"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMiddleName(e.target.value)}
                                />
                            </div>
                            <div className="system--users__component--widget__add--header__text">Email</div>
                            <div className="system--users__component--widget__add--header__input">
                                <InputText
                                    id="login_email"
                                    value={email}
                                    placeholder="email"
                                    onBlur={validateEmail}
                                    autoComplete="email"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                />
                                {emailError ? (
                                    <p className="system--users__component--widget__add--header__input--error_message">
                                        {emailError}
                                    </p>
                                ) : null}
                            </div>
                            <div className="system--users__component--widget__add--header__text">Роль</div>
                            <div className="system--users__component--widget__add--header__role">
                                <div className="system--users__component--widget__Edit--block__role">
                                    <div className="system--users__component--widget__Edit--block__role--text">
                                        {roleNewUser
                                            ? allRoles!.find((xx) => xx.id === roleNewUser)!.roleName
                                            : "Выберите роль"}
                                    </div>
                                    <button
                                        onClick={() =>
                                            viewBlock(
                                                "WidgetRoleAdd",
                                                "WidgetRoleArrowSubAdd",
                                                "WidgetRoleArrowRightAdd"
                                            )
                                        }
                                        className="add__edit--role__component__widget--button__show--more"
                                    >
                                        <img
                                            src={ArrowRight}
                                            id={"WidgetRoleArrowSubAdd"}
                                            style={{ display: "none" }}
                                        />
                                        <img src={ArrowSub} id={"WidgetRoleArrowRightAdd"} />
                                    </button>
                                </div>
                                <div
                                    className="system--users__component--table__content--td__role--choise"
                                    id={"WidgetRoleAdd"}
                                    style={{ display: "none" }}
                                >
                                    <div
                                        className="system--users__component--widget__Edit--block__role--absolut"
                                        style={{ background: "white" }}
                                    >
                                        {allRoles &&
                                            allRoles.map((role) => (
                                                <div
                                                    className="system--users__component--table__content--td__role--choise__one"
                                                    onClick={() =>
                                                        handleRoleChangeRoleNewPerson(
                                                            "WidgetRoleAdd",
                                                            "WidgetRoleArrowSubAdd",
                                                            "WidgetRoleArrowRightAdd",
                                                            role.id
                                                        )
                                                    }
                                                >
                                                    {role.roleName}
                                                </div>
                                            ))}
                                    </div>
                                </div>
                                {roleNewUserError ? (
                                    <p className="system--users__component--widget__add--header__input--error_message">
                                        {roleNewUserError}
                                    </p>
                                ) : null}
                            </div>
                            <div>
                                <div className="system--users__component--widget__button">
                                    <button
                                        data-testId="button-add_user_form-confirm"
                                        onClick={addPersonFunc}
                                        className="add__edit--role__component--buttons__create"
                                    >
                                        Подтвердить
                                    </button>
                                    <button
                                        data-testId="button-add_user_form-cancel"
                                        onClick={() => closeAdd()}
                                        className="add__edit--role__component--buttons__cansel"
                                    >
                                        Отмена
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <></>
            )}
            {showDeleteMessage && (
                <ConfirmationWindow
                    headerText="Удаление пользователя"
                    bodyText="Вы уверены, что хотите удалить выбранных пользователей?"
                    confirmText="Удалить"
                    cancelText="Отмена"
                    onConfirm={deletePersonFunc}
                    onCancel={() => {
                        setShowDeleteMessage(false);
                        resetChecks();
                    }}
                />
            )}
            {editWidgetShow ? (
                <div className="system--users__component--widget__Edit">
                    <div className="system--users__component--widget__Edit--block">
                        <div className="system--users__component--widget__Edit--block__header">
                            <div className="system--users__component--widget__Edit--block__header--name">
                                Редактирование пользователя
                            </div>
                            <div className="add__edit--role__component--header__img">
                                <img src={Cross} onClick={closeFunc} />
                            </div>
                        </div>
                        <div className="system--users__component--widget__Edit--block__role--new">
                            <div className="system--users__component--widget__Edit--block__role--name__h">
                                Системная роль
                            </div>
                            <div className="system--users__component--widget__Edit--block__right">
                                <div className="system--users__component--widget__Edit--block__role--new">
                                    <div>{currentEditUser?.systemRole.name}</div>
                                    <button
                                        onClick={() =>
                                            viewBlock("WidgetRole", "WidgetRoleArrowSub", "WidgetRoleArrowRight")
                                        }
                                        className="add__edit--role__component__widget--button__show--more"
                                    >
                                        <img src={ArrowRight} id={"WidgetRoleArrowSub"} style={{ display: "none" }} />
                                        <img src={ArrowSub} id={"WidgetRoleArrowRight"} />
                                    </button>
                                </div>
                                <div
                                    className="system--users__component--table__content--td__role--choise"
                                    id={"WidgetRole"}
                                    style={{ display: "none" }}
                                >
                                    <div
                                        className="system--users__component--widget__Edit--block__role--absolut"
                                        style={{ background: "white" }}
                                    >
                                        {allRoles &&
                                            allRoles.map((role) => (
                                                <div
                                                    className="system--users__component--table__content--td__role--choise__one"
                                                    onClick={() =>
                                                        handleRoleChangeWidget(
                                                            "WidgetRole",
                                                            "WidgetRoleArrowSub",
                                                            "WidgetRoleArrowRight",
                                                            role.id,
                                                            currentEditUser!.id
                                                        )
                                                    }
                                                >
                                                    {role.roleName}
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="system--users__component--widget__Edit--block__role--new">
                            <div className="system--users__component--widget__Edit--block__role--name__h">Статус</div>
                            <div className="system--users__component--widget__Edit--block__right">
                                <div className="system--users__component--widget__Edit--block__status">
                                    <div className="system--users__component--widget__Edit--block__status--width">
                                        <div
                                            className="system--users__component--table__content--td_status system--users__component--widget__Edit--block__status--shoise"
                                            style={{
                                                border: "1px solid " + currentEditUser!.status.color,
                                                color: currentEditUser!.status.color,
                                            }}
                                        >
                                            {currentEditUser?.status?.name}
                                        </div>
                                    </div>
                                </div>
                                {currentEditUser!.status.code !== "Blocked" &&
                                    currentEditUser!.status.code !== "PendingActivation" ? (
                                    <div
                                        onClick={() => changeStatus(true)}
                                        className="system--users__component--widget__Edit--block__style--action"
                                    >
                                        <img src={Cross} />
                                    </div>
                                ) : (

                                    <Button
                                        id='button-add-chek-mark'
                                        image={<DeletedStatus />}
                                        callBack={() => changeStatus(false)}
                                        className="system--users__component--widget__Edit--block__style--action__aprove"
                                        buttonSize="medium"
                                    />
                                )}
                            </div>
                        </div>
                        <div className="system--users__component--widget__button">
                            <button
                                data-testId="button-edit_user_form-save"
                                onClick={saveUser}
                                className="add__edit--role__component--buttons__create"
                            >
                                Сохранить
                            </button>
                            <button
                                data-testId="button-edit_user_form-cancel"
                                onClick={closeFunc}
                                className="add__edit--role__component--buttons__cansel"
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <></>
            )}
            <div className="roles--system__component__buttons--block">
                <div className="roles--system__component__buttons--block__size">
                    {selectedUsersCount > 0 ? (
                        <div className="roles--system__component__buttons--block__size--hiden">
                            {showDeleteButton && <BtnDelete onClickFunc={showDeleteFunc} permission={true} />}
                            {showEditButton && (
                                <button className="roles--system__component__buttons--block__edit" onClick={editPerson}>
                                    <img src={EditIcon} />
                                </button>
                            )}
                        </div>
                    ) : (
                        <></>
                    )}
                    <BtnAdd onClickFunc={() => setShowAddWidget(true)} type="add" permission={true} />
                </div>
            </div>
            <div>
                <table className="system--users__component--table">
                    <tr className="system--users__component--table__tr">
                        <th className="system--users__component--table__content--td__checkbox"></th>
                        <th
                            style={
                                currentSort.type === SortType.name
                                    ? { backgroundColor: "rgba(20, 156, 206, 0.07)" }
                                    : {}
                            }
                        >
                            <div
                                className="system--users__component--table__content--th__datentry"
                                onClick={() => setSort(SortType.name)}
                            >
                                Пользователь
                                {currentSort.type === SortType.name && currentSort.asc ? (
                                    <div className="system--users__component--table__content--th__datentry--img">
                                        <img src={ArrowUp} />
                                    </div>
                                ) : currentSort.type === SortType.name && !currentSort.asc ? (
                                    <div className="system--users__component--table__content--th__datentry--img">
                                        <img src={ArrowDown} />
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </th>
                        <th
                            style={
                                currentSort.type === SortType.role
                                    ? { backgroundColor: "rgba(20, 156, 206, 0.07)" }
                                    : {}
                            }
                        >
                            <div
                                className="system--users__component--table__content--th__datentry"
                                onClick={() => setSort(SortType.role)}
                            >
                                Системная роль
                                {currentSort.type === SortType.role && currentSort.asc ? (
                                    <div className="system--users__component--table__content--th__datentry--img">
                                        <img src={ArrowUp} />
                                    </div>
                                ) : currentSort.type === SortType.role && !currentSort.asc ? (
                                    <div className="system--users__component--table__content--th__datentry--img">
                                        <img src={ArrowDown} />
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </th>
                        <th
                            style={
                                currentSort.type === SortType.dateEntry
                                    ? { backgroundColor: "rgba(20, 156, 206, 0.07)" }
                                    : {}
                            }
                        >
                            <div
                                onClick={() => setSort(SortType.dateEntry)}
                                className="system--users__component--table__content--th__datentry"
                            >
                                <div>Дата последнего входа</div>
                                {currentSort.type === SortType.dateEntry && currentSort.asc ? (
                                    <div className="system--users__component--table__content--th__datentry--img">
                                        <img src={ArrowUp} />
                                    </div>
                                ) : currentSort.type === SortType.dateEntry && !currentSort.asc ? (
                                    <div className="system--users__component--table__content--th__datentry--img">
                                        <img src={ArrowDown} />
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </th>
                        <th
                            style={
                                currentSort.type === SortType.dateUpdate
                                    ? { backgroundColor: "rgba(20, 156, 206, 0.07)" }
                                    : {}
                            }
                        >
                            <div
                                onClick={() => setSort(SortType.dateUpdate)}
                                className="system--users__component--table__content--th__datentry"
                            >
                                <div>Дата обновления</div>
                                {currentSort.type === SortType.dateUpdate && currentSort.asc ? (
                                    <div className="system--users__component--table__content--th__datentry--img">
                                        <img src={ArrowUp} />
                                    </div>
                                ) : currentSort.type === SortType.dateUpdate && !currentSort.asc ? (
                                    <div className="system--users__component--table__content--th__datentry--img">
                                        <img src={ArrowDown} />
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </th>
                        <th>
                            <div
                                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                className="system--users__component--table__content--th__status"
                            >
                                <div className="system--users__component--table__content--th__status--header">
                                    {currentSort.type === SortType.status &&
                                        currentSort.status !== undefined &&
                                        currentSort.status
                                        ? currentSort.status.name
                                        : "Статус"}
                                </div>
                                {showStatusDropdown ? (
                                    <div className="system--users__component--table__content--td__status--choise">
                                        <div className="system--users__component--widget__Edit--block__status--absolut">
                                            {userStatuses.map((xx) => (
                                                <div
                                                    onClick={() => (
                                                        setStatusSort(xx.code),
                                                        setShowStatusDropdown(!showStatusDropdown)
                                                    )}
                                                    className="system--users__component--table__content--td__role--choise__one"
                                                >
                                                    {xx.name}
                                                </div>
                                            ))}
                                            <div
                                                onClick={() => (
                                                    setStatusSort(undefined), setShowStatusDropdown(!showStatusDropdown)
                                                )}
                                                className="system--users__component--table__content--td__role--choise__one--clean"
                                            >
                                                Сбросить
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </th>
                    </tr>
                    {allUsers?.map((user) => (
                        <tr className="system--users__component--table__content--tr">
                            <td className="system--users__component--table__content--td__checkbox">
                                <input
                                    type="checkbox"
                                    checked={user.check}
                                    onClick={() => changeCheck(user.user.id, !user.check)}
                                />
                            </td>
                            <td
                                style={
                                    currentSort.type === SortType.name
                                        ? { backgroundColor: "rgba(20, 156, 206, 0.07)" }
                                        : {}
                                }
                            >
                                <div className="system--users__component--table__content--td__block--img">
                                    <div className="system--users__component--table__content--td__img">
                                        <Avatar size={AvatarSize.s} userId={user.user.id} />
                                    </div>
                                    <div className="system--users__component--table__content--td__block--name__email">
                                        <div className="system--users__component--table__content--td__block--name">
                                            {fullNameFormat(user.user.user, "s N M")}
                                        </div>
                                        <div className="system--users__component--table__content--td__block--email">
                                            {user.user.user.email}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td
                                className="system--users__component--table__content--td__block--role"
                                style={
                                    currentSort.type === SortType.role
                                        ? { backgroundColor: "rgba(20, 156, 206, 0.07)" }
                                        : {}
                                }
                            >
                                <div className="system--users__component--table__content--td__role--block">
                                    <div className="system--users__component--table__content--td__role">
                                        <div className="system--users__component--table__content--td__role--name">
                                            {user.user.systemRole.name}
                                        </div>
                                        <button
                                            onClick={() =>
                                                viewBlock(
                                                    "SystemRole-" + String(user?.user.id),
                                                    "SystemGroupArrowSub" + String(user?.user.id),
                                                    "SystemGroupArrowRight" + String(user?.user.id)
                                                )
                                            }
                                            className="add__edit--role__component--button__show--more"
                                        >
                                            <img
                                                src={ArrowRight}
                                                id={"SystemGroupArrowSub" + String(user?.user.id)}
                                                style={{ display: "none" }}
                                            />
                                            <img src={ArrowSub} id={"SystemGroupArrowRight" + String(user?.user.id)} />
                                        </button>
                                    </div>
                                    <div
                                        className="system--users__component--table__content--td__role--choise"
                                        style={{ display: "none" }}
                                        id={"SystemRole-" + String(user?.user.id)}
                                    >
                                        <div
                                            className="system--users__component--widget__Edit--block__role--absolut"
                                            style={{ background: "white" }}
                                        >
                                            {allRoles &&
                                                allRoles.map((role) => (
                                                    <div
                                                        className="system--users__component--table__content--td__role--choise__one"
                                                        onClick={() =>
                                                            handleRoleChange(
                                                                "SystemRole-" + String(user?.user.id),
                                                                role.id,
                                                                user?.user.id
                                                            )
                                                        }
                                                    >
                                                        {role.roleName}
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td
                                style={
                                    currentSort.type === SortType.dateEntry
                                        ? { backgroundColor: "rgba(20, 156, 206, 0.07)" }
                                        : {}
                                }
                            >
                                <div className="system--users__component--table__content--td__date">
                                    {dateToString(user.user.dateLastIn)}
                                </div>
                            </td>
                            <td
                                style={
                                    currentSort.type === SortType.dateUpdate
                                        ? { backgroundColor: "rgba(20, 156, 206, 0.07)" }
                                        : {}
                                }
                            >
                                <div className="system--users__component--table__content--td__date">
                                    {dateToString(user.user.dateUpdate)}
                                </div>
                            </td>
                            <td>
                                <div
                                    className="system--users__component--table__content--td_status"
                                    style={{
                                        border: "1px solid " + user.user.status.color,
                                        color: user.user.status.color,
                                    }}
                                >
                                    {user.user.status.name}
                                </div>
                            </td>
                        </tr>
                    ))}
                </table>
            </div>
        </div>
    );
};

export default SystemUsers;
